import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type Kayttaja, type KaannosTarkistus as Tarkistus } from "./api";
import { KAANNOKSET, LUKITTU_SANASTO, type KaannosAvain } from "./kaannokset";
import { t, virheTeksti, KIELEN_NIMI, kaannos, valittavatKielet, type Kieli } from "./kieli";
import { naytaPvmKello } from "./pvm";
import { KONTEKSTI_EN } from "./kaannokset_konteksti_en";
import { useNayttotila } from "./nayttotila";

/** Kielet, joiden teksti on konekäännös omassa tiedostossaan (kaannokset_lt.ts, kaannokset_et.ts): jokainen rivi on
 *  «tarkistamatta» kunnes äidinkielinen tarkistaja merkitsee sen, ja kääntämätön avain jätetään listalta pois. */
const KONEKAANNOS: ReadonlySet<string> = new Set(["lt", "et"]);

/* ═══════════════════════════════════════════════════════════
   T440 · Käännösten tarkistus (206; Toni 8.10.2026 «Ok, jatka T440:llä»).

   Tarkistaja käy läpi oman kielensä käännökset puhelimella: rivillä konteksti (missä teksti näkyy), suomi ja nykyinen
   käännös. «Oikein» kuittaa sellaisenaan; «Korjaa» tallentaa korjauksen, joka odottaa hyväksyntää eikä mene käyttöön
   ennen sitä. Hyväksyjä näkee kaikki kielet ja hyväksyy tai hylkää korjaukset. Hyväksytty korjaus viedään
   käännöstaulukkoon seuraavassa versiossa (`npm run kaannoskorjaukset`).

   Tarkistus koskee sitä tekstiä joka oli ruudulla (ehdotus tallentuu riville): jos käännös muuttuu myöhemmin, rivi
   näkyy taas tarkistamattomana — vanha kuittaus ei kata uutta tekstiä.

   Toni 10.10.2026 «käännökset: tavalliset automaattisesti hyväksytyiksi, termit minulle»: rivi ilman `termi`-merkintää
   (kaannokset.ts) ja ilman tarkistusta näkyy «hyväksytty automaattisesti». Tila johdetaan merkinnästä eikä tallennu
   kantaan, joten ihmisen tarkistus (kaannos_tarkistus) erottuu siitä aina. Termit ovat listan alussa.

   Lähdekieli (Toni 11.10.2026 «Lähdekieli valittavaksi… Ei Gintaras osaa suomea… EN pakollinen»): tarkistaja vertaa
   käännöstä suomeen TAI englantiin. Oletus: suomenkielinen tunnus näkee suomen, muut englannin; valinta tallentuu
   tunnukselle. Rivin konteksti, lähdeteksti ja lukittu sanasto näytetään lähdekielellä — englanninkielinen konteksti on
   kaannokset_konteksti_en.ts:ssä (avain = suomenkielinen konteksti), ja kommitoi.sh pysäyttää rivin ilman sitä.
   ═══════════════════════════════════════════════════════════ */

type Rivi = { k: string; fi: string; termi?: true } & Partial<Record<Kieli, string>>;
type Tila = "tarkistamatta" | "muuttunut" | "automaattinen" | "oikein" | "odottaa" | "hyvaksytty" | "ei_koodissa" | "hylatty";
type Suodatin = "tarkistamatta" | "odottaa" | "kaikki";

const TILAN_AVAIN: Record<Tila, KaannosAvain> = {
  tarkistamatta: "kt.tila.tarkistamatta", muuttunut: "kt.tila.muuttunut", automaattinen: "kt.tila.automaattinen", ei_koodissa: "kt.tila.ei_koodissa", oikein: "kt.tila.oikein",
  odottaa: "kt.tila.odottaa", hyvaksytty: "kt.tila.hyvaksytty", hylatty: "kt.tila.hylatty",
};

export default function KaannosTarkistus({ kayttaja }: { kayttaja: Kayttaja }) {
  const kielet = valittavatKielet().filter((x) => x !== "fi");
  const [kieli, setKieli] = useState<Kieli | null>(
    kayttaja.kieli && kayttaja.kieli !== "fi" ? (kayttaja.kieli as Kieli) : (kielet[0] ?? null));
  const [rivit, setRivit] = useState<Tarkistus[] | null>(null);
  const [saaHyvaksya, setSaaHyvaksya] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [suodatin, setSuodatin] = useState<Suodatin>("tarkistamatta");
  const [auki, setAuki] = useState<string | null>(null);
  const [korjaus, setKorjaus] = useState("");
  const [kommentti, setKommentti] = useState("");
  const [tyo, setTyo] = useState(false);
  // Tallennus olemassa olevalla näyttötilalla: «auki» = englanti, «kiinni» = suomi, ei valintaa = oletus kielestä.
  const [lahdeValinta, asetaLahde] = useNayttotila("ohje", "kaannokset_lahde_en");
  const oletusLahde: "fi" | "en" = (kayttaja.kieli ?? "fi") === "fi" ? "fi" : "en";
  // Tarkistettava kieli ei voi olla oma lähteensä: englannin tarkistaja vertaa aina suomeen.
  const lahde: "fi" | "en" = kieli === "en" ? "fi" : lahdeValinta === "auki" ? "en" : lahdeValinta === "kiinni" ? "fi" : oletusLahde;

  const lataa = useCallback(async () => {
    if (!kieli) return;
    try {
      const r = await api.kaannosTarkistukset(kieli);
      setRivit(r.rivit); setSaaHyvaksya(r.saa_hyvaksya); setVirhe(null);
    } catch (e) { setRivit([]); setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
  }, [kieli]);
  useEffect(() => { void lataa(); }, [lataa]);

  // Uusin tarkistus per avain (rivit tulevat uusin ensin avaimen sisällä).
  const uusin = useMemo(() => {
    const m = new Map<string, Tarkistus>();
    for (const r of rivit ?? []) if (!m.has(r.avain)) m.set(r.avain, r);
    return m;
  }, [rivit]);

  const kaikki = useMemo(() => (Object.entries(KAANNOKSET) as [KaannosAvain, Rivi][]).map(([avain, r]) => {
    const nyt = kieli ? kaannos(avain, kieli) ?? "" : "";
    const u = uusin.get(avain);
    // Hyväksytty korjaus joka on viety koodiin (korjaus = nykyinen teksti) on tarkistettu, ei «muuttunut tarkistuksen jälkeen».
    const viety = !!u && u.tulos === "korjattu" && u.hyvaksynta === "hyvaksytty" && u.korjaus === nyt;
    // Liettua (11.10.2026) ja viro (18.10.2026): konekäännös on aina «tarkistamatta», myös tavallinen käyttöliittymäteksti.
    const tila: Tila = !u ? (r.termi || KONEKAANNOS.has(kieli ?? "") ? "tarkistamatta" : "automaattinen") : viety ? "hyvaksytty" : u.ehdotus !== nyt ? "muuttunut"
      : u.tulos === "oikein" ? "oikein" : u.hyvaksynta === "odottaa" ? "odottaa"
      // Hyväksytty korjaus jota ei viety koodiin (pääChat 10.10.2026, protot 8.10. «Deficiencies» kumottiin): oma tila, ei «hyväksytty».
      : u.hyvaksynta === "hyvaksytty" ? "ei_koodissa" : "hylatty";
    return { avain, r, nyt, u, tila };
  }).filter((x) => !KONEKAANNOS.has(kieli ?? "") || x.nyt !== "").sort((a, b) => Number(!!b.r.termi) - Number(!!a.r.termi)), [kieli, uusin]);

  // Kolme lukua erikseen: ihmisen katsomat, säännöllä hyväksytyt ja tarkistamattomat termit.
  const tarkistettu = kaikki.filter((x) => !["tarkistamatta", "muuttunut", "automaattinen"].includes(x.tila)).length;
  const automaattisia = kaikki.filter((x) => x.tila === "automaattinen").length;
  const termejaAuki = kaikki.filter((x) => x.r.termi && (x.tila === "tarkistamatta" || x.tila === "muuttunut")).length;
  const nakyvat = kaikki.filter((x) => suodatin === "kaikki" ? true
    : suodatin === "odottaa" ? x.tila === "odottaa" : (x.tila === "tarkistamatta" || x.tila === "muuttunut"));

  async function kirjaa(avain: string, ehdotus: string, tulos: "oikein" | "korjattu") {
    if (!kieli || tyo) return;
    setTyo(true); setVirhe(null);
    try {
      await api.tarkistaKaannos({ avain, kieli, ehdotus, tulos, ...(tulos === "korjattu" ? { korjaus, kommentti } : {}) });
      setAuki(null); setKorjaus(""); setKommentti("");
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
    finally { setTyo(false); }
  }
  async function hyvaksy(id: number, kylla: boolean) {
    if (tyo) return;
    setTyo(true); setVirhe(null);
    try { await api.hyvaksyKaannos(id, kylla); await lataa(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
    finally { setTyo(false); }
  }

  return (
    <section className="nakyma kaannokset" data-nakyma-kaannokset={kieli ?? ""} data-kt-lahde={lahde}>
      <header className="head">
        <div className="head__rivi"><h1>{t("kt.otsikko")}</h1></div>
      </header>
      <p className="dim" style={{ maxWidth: 640 }}>{t("kt.ohje")}</p>

      {saaHyvaksya && (
        <label className="dim">{t("kieli.otsikko")}{" "}
          <select value={kieli ?? ""} onChange={(e) => setKieli(e.target.value as Kieli)} data-kt-kieli="">
            {kielet.map((x) => <option key={x} value={x}>{KIELEN_NIMI[x]}</option>)}
          </select>
        </label>
      )}

      {kieli !== "en" && (
        <label className="dim" style={{ marginLeft: 12 }}>{t("kt.lahdekieli")}{" "}
          <select value={lahde} onChange={(e) => asetaLahde(e.target.value === "en" ? "auki" : "kiinni")} data-kt-lahdekieli="">
            <option value="fi">{KIELEN_NIMI.fi}</option>
            <option value="en">{KIELEN_NIMI.en}</option>
          </select>
        </label>
      )}

      <details className="kaannokset__sanasto" style={{ margin: "10px 0" }}>
        <summary>{t("kt.sanasto")}</summary>
        <ul data-kt-sanasto={lahde}>{LUKITTU_SANASTO.map((s) => (
          <li key={s.fi}>{lahde === "en"
            ? <><b>{s.en}</b> <span className="dim">({t("kt.suomeksi")}: {s.fi} · {s.paatos})</span></>
            : <><b>{s.fi}</b> → <b>{s.en}</b> <span className="dim">({s.paatos})</span></>}</li>))}</ul>
      </details>

      <p data-kt-edistyma={`${tarkistettu}/${kaikki.length}`} data-kt-yhteenveto={`${tarkistettu}/${automaattisia}/${termejaAuki}`}>
        {t("kt.yhteenveto", { ihminen: tarkistettu, auto: automaattisia, termit: termejaAuki })}</p>
      <div className="suodatinrivi" role="group">
        {(["tarkistamatta", "odottaa", "kaikki"] as const).map((s) => (
          <button key={s} className={"nappi" + (suodatin === s ? " nappi--paa" : " nappi--kevyt")} aria-pressed={suodatin === s}
                  data-kt-suodatin={s} onClick={() => setSuodatin(s)}>{t(`kt.suodatin.${s}` as KaannosAvain)}</button>
        ))}
      </div>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {rivit === null && <p className="dim">{t("yleinen.ladataan")}</p>}
      {rivit && nakyvat.length === 0 && <p className="dim" data-tyhja-tila="">{t("kt.ei_riveja")}</p>}

      <ul className="kaannokset__lista">
        {nakyvat.slice(0, 200).map(({ avain, r, nyt, u, tila }) => (
          <li key={avain} className="kaannokset__rivi" data-kaannosrivi={avain} data-kt-tila={tila} data-kt-termi={r.termi ? "1" : "0"}
              style={{ borderBottom: "1px solid var(--viiva)", padding: "10px 0" }}>
            <div className="dim" data-kt-konteksti={lahde === "en" && KONTEKSTI_EN[r.k] ? "en" : "fi"}>{lahde === "en" ? KONTEKSTI_EN[r.k] ?? r.k : r.k} · <span className="mono">{avain}</span></div>
            {/* Kielitunnus omaan kiinteän levyiseen sarakkeeseen, teksti samaan pystylinjaan joka kielellä (Toni 9.10.2026). */}
            <div className="kaannokset__kielet">
              <span className="kaannokset__kieli" data-kt-lahderivi={lahde}>{lahde.toUpperCase()}</span><span>{lahde === "en" ? r.en ?? r.fi : r.fi}</span>
              <span className="kaannokset__kieli">{(kieli ?? "").toUpperCase()}</span><span>{nyt || <i className="dim">{t("kt.puuttuu")}</i>}</span>
            </div>
            <div className="dim">
              {t(TILAN_AVAIN[tila])}
              {u && ` · ${u.tarkistaja} ${naytaPvmKello(u.tarkistettu) ?? ""}`}
              {u?.tulos === "korjattu" && <> · {t("kt.korjaus")}: «{u.korjaus}»</>}
              {u?.kommentti && <> · {u.kommentti}</>}
              {u?.hyvaksyja && <> · {u.hyvaksyja}</>}
            </div>
            {auki === avain ? (
              <div className="kaannokset__lomake">
                <label>{t("kt.korjattu_teksti")}
                  <textarea rows={2} value={korjaus} onChange={(e) => setKorjaus(e.target.value)} data-kt-korjausteksti="" /></label>
                <label>{t("kt.kommentti")}
                  <input value={kommentti} onChange={(e) => setKommentti(e.target.value)} /></label>
                <button className="nappi nappi--paa" disabled={tyo || !korjaus.trim()} data-kt-tallenna=""
                        onClick={() => void kirjaa(avain, nyt, "korjattu")}>{t("pc.tallenna")}</button>{" "}
                <button className="nappi nappi--kevyt" onClick={() => setAuki(null)}>{t("hl.peruuta")}</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                {nyt && (tila === "tarkistamatta" || tila === "muuttunut") && (
                  <button className="nappi" disabled={tyo} data-kt-oikein="" onClick={() => void kirjaa(avain, nyt, "oikein")}>{t("kt.oikein")}</button>)}
                <button className="nappi nappi--kevyt" disabled={tyo} data-kt-korjaa=""
                        onClick={() => { setAuki(avain); setKorjaus(nyt); setKommentti(""); }}>{t("kt.korjaa")}</button>
                {saaHyvaksya && u && tila === "odottaa" && <>
                  <button className="nappi nappi--paa" disabled={tyo} data-kt-hyvaksy="" onClick={() => void hyvaksy(u.id, true)}>{t("kt.hyvaksy")}</button>
                  <button className="nappi nappi--kevyt" disabled={tyo} data-kt-hylkaa="" onClick={() => void hyvaksy(u.id, false)}>{t("kt.hylkaa")}</button>
                </>}
              </div>
            )}
          </li>
        ))}
      </ul>
      {nakyvat.length > 200 && <p className="dim">{t("kt.n_lisaa", { n: nakyvat.length - 200 })}</p>}
    </section>
  );
}
